import { EventEmitter } from "events";

import {
  CacheEntryExpiredEvent,
  CacheEntryExpiringEvent,
  CacheEntryPriority,
} from "./types";

type CacheEntry = {
  key: any;
  value: any;
  priority: CacheEntryPriority;
  size: number;
  lastAccessed: number;
};

interface MemoryCacheEvents {
  expiring: (event: CacheEntryExpiringEvent) => void;
  expired: (event: CacheEntryExpiredEvent) => void;
}

export declare interface MemoryCache {
  on<E extends keyof MemoryCacheEvents>(
    event: E,
    listener: MemoryCacheEvents[E]
  ): this;

  off<E extends keyof MemoryCacheEvents>(
    event: E,
    listener: MemoryCacheEvents[E]
  ): this;

  emit<E extends keyof MemoryCacheEvents>(
    event: E,
    ...args: Parameters<MemoryCacheEvents[E]>
  ): boolean;
}

const FALLBACK_DEVICE_MEMORY_GB = 2; // in gigabytes

export class MemoryCache extends EventEmitter {
  private static s_instance: MemoryCache;

  private readonly _entries = new Map<any, CacheEntry>();

  private _size = 0;

  // In Bytes
  private readonly _maxSize =
    ((navigator as any).deviceMemory ?? FALLBACK_DEVICE_MEMORY_GB) *
    1024 *
    1024 *
    1024 *
    0.1; // take 10%

  private _compactIfNeededTimeoutHandle: ReturnType<typeof setTimeout> = null;
  private readonly _compactIfNeededThrottleMs = 1000;
  private readonly _compactionPercentage = 5;

  private constructor() {
    super();
  }

  public get size() {
    return this._size;
  }

  public static get instance() {
    if (!MemoryCache.s_instance) {
      MemoryCache.s_instance = new MemoryCache();
    }

    return MemoryCache.s_instance;
  }

  public get(key: any) {
    const entry = this._entries.get(key);
    if (entry) {
      entry.lastAccessed = Date.now();
      return entry.value;
    }
  }

  public has(key: any) {
    return this._entries.has(key);
  }

  public set(key: any, value: any, priority: CacheEntryPriority, size: number) {
    if (this._entries.has(key)) {
      return;
    }

    const entry = {
      key,
      value,
      priority,
      size,
      lastAccessed: Date.now(),
    };
    this._entries.set(key, entry);
    this._size += entry.size;

    this.startCompactIfNeeded();
  }

  private startCompactIfNeeded() {
    if (this._size <= this._maxSize) {
      return;
    }

    if (!this._compactIfNeededTimeoutHandle) {
      this._compactIfNeededTimeoutHandle = setTimeout(() => {
        this._compactIfNeededTimeoutHandle = null;
        this.compactIfNeeded();
      }, this._compactIfNeededThrottleMs);
    }
  }

  private compactIfNeeded() {
    const lowWatermark =
      this._maxSize * ((100 - this._compactionPercentage) / 100);

    if (this._size > lowWatermark) {
      this.compact(this._size - lowWatermark);
    }
  }

  private compact(removalSizeTarget: number) {
    const candidateEntries = new Array<CacheEntry>();

    for (const entry of this._entries.values()) {
      if (entry.priority === CacheEntryPriority.NORMAL) {
        candidateEntries.push(entry);
      }
    }

    // LRU: sort most recently used to least recently used
    candidateEntries.sort((a, b) => b.lastAccessed - a.lastAccessed);

    let removedSize = 0;
    while (removedSize < removalSizeTarget && candidateEntries.length > 0) {
      const entry = candidateEntries.pop();

      if (this.tryExpireEntry(entry)) {
        removedSize += entry.size;
      }
    }
  }

  private tryExpireEntry(entry: CacheEntry) {
    const expiringEvent = new CacheEntryExpiringEvent(entry.key);

    this.emit("expiring", expiringEvent);

    if (!expiringEvent.isCanceled) {
      this.expireEntry(entry);
      return true;
    } else {
      return false;
    }
  }

  private expireEntry(entry: CacheEntry) {
    this._entries.delete(entry.key);
    this._size -= entry.size;

    this.emit("expired", { key: entry.key });
  }
}
